import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { Subject } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';
import { CheckoutService } from '../../services/checkout.service';
import { PAYMENTS_CONFIG } from '../../payments.config';
import { PaymentConfig } from '../../models/payment.models';

interface CheckoutResponse {
  id: string;
  integrity: string;
  merchantReference: string;
}

export interface PaymentWidgetState {
  isLoading: boolean;
  error: string | null;
  isScriptLoaded: boolean;
  isFormReady: boolean;
}

declare global {
  interface Window {
    wpwlOptions?: { onReady: () => void };
  }
}

/**
 * Zimswitch payments rendered via the OPPWA copy-and-pay widget, which is
 * injected into the page at runtime.
 */
@Component({
  selector: 'lib-zimswitch',
  standalone: true,
  imports: [CommonModule, NzButtonModule, NzSpinModule],
  templateUrl: './zimswitch.html',
  styleUrl: './zimswitch.css',
})
export class Zimswitch implements OnInit, OnDestroy, OnChanges {
  readonly payloadConfig = input.required<PaymentConfig>();

  private readonly checkoutService = inject(CheckoutService);
  private readonly config = inject(PAYMENTS_CONFIG);
  private readonly renderer = inject(Renderer2);

  private readonly configChange$ = new Subject<void>();
  private previousAmount: number | undefined;
  private readonly WIDGET_LOAD_TIMEOUT = 30000;
  private formCheckInterval: any;

  user: any;
  paymentRedirectUrl = this.config.paymentRedirectUrl;
  referenceNumber = '';

  readonly paymentContainer = viewChild.required<ElementRef>('paymentContainer');

  readonly widgetState = signal<PaymentWidgetState>({
    isLoading: true,
    error: null,
    isScriptLoaded: false,
    isFormReady: false,
  });

  private patchWidgetState(patch: Partial<PaymentWidgetState>): void {
    this.widgetState.update((state) => ({ ...state, ...patch }));
  }

  constructor() {
    const rawUser = localStorage.getItem(this.config.userStorageKey ?? 'auth_user');
    this.user = rawUser ? JSON.parse(rawUser) : null;

    this.configChange$.pipe(debounceTime(1500)).subscribe(() => this.prepareCheckout());
  }

  ngOnInit(): void {
    this.setupWidgetReadyListener();
    if (this.payloadConfig()?.feeId) {
      this.prepareCheckout();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['payloadConfig'] && this.payloadConfig().amount !== this.previousAmount) {
      this.previousAmount = this.payloadConfig().amount;
      if (this.payloadConfig().isWallet && !this.referenceNumber) {
        this.generateReferenceWithoutFee(this.payloadConfig().feeId, this.payloadConfig().amount);
      } else {
        this.configChange$.next();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.formCheckInterval) clearInterval(this.formCheckInterval);
    window.removeEventListener('wpwlOptionsReady', this.handleWidgetReady);
    this.configChange$.complete();
  }

  private generateReferenceWithoutFee(feeId: string | null = null, amount = 0): void {
    this.patchWidgetState({ isLoading: true });
    const payload: any = {};

    if (this.user) {
      payload.userId = this.user.id;
      payload.feeId = feeId;
      payload.callbackUrl = this.payloadConfig().isWallet
        ? this.checkoutService.frontendUrl + 'wallet'
        : this.checkoutService.frontendUrl;
      payload.paymentChannel = this.payloadConfig().paymentChannel;
      if (amount > 0) payload.amount = amount;
    }

    this.checkoutService.generateMerchantReferenceWithoutFee(payload).subscribe((res: any) => {
      this.referenceNumber = res.invoiceNumber;
      this.payloadConfig().merchantReference = res.invoiceNumber;
      this.configChange$.next();
    });
  }

  private setupWidgetReadyListener(): void {
    window.wpwlOptions = { onReady: () => this.handleFormReady() };
    window.addEventListener('wpwlOptionsReady', this.handleWidgetReady);
    this.startFormCheck();
  }

  private startFormCheck(): void {
    if (this.formCheckInterval) clearInterval(this.formCheckInterval);

    this.formCheckInterval = setInterval(() => {
      const form = document.querySelector('.wpwl-form');
      const inputs = document.querySelector('.wpwl-control');
      if (form && inputs) {
        this.handleFormReady();
        clearInterval(this.formCheckInterval);
      }
    }, 500);

    setTimeout(() => {
      if (this.formCheckInterval) clearInterval(this.formCheckInterval);
    }, this.WIDGET_LOAD_TIMEOUT);
  }

  private handleWidgetReady = (): void => {
    this.patchWidgetState({ isScriptLoaded: true });
  };

  private handleFormReady = (): void => {
    this.patchWidgetState({ isFormReady: true, isLoading: false });
  };

  getLoadingMessage(): string {
    const state = this.widgetState();
    if (!state.isScriptLoaded) return 'Loading payment widget...';
    if (!state.isFormReady) return 'Initializing payment form...';
    return 'Loading payment...';
  }

  async retryPaymentWidget(): Promise<void> {
    this.widgetState.set({ isLoading: true, error: null, isScriptLoaded: false, isFormReady: false });
    try {
      this.prepareCheckout();
    } catch {
      this.patchWidgetState({ error: 'Failed to load payment widget. Please try again.', isLoading: false });
    }
  }

  private prepareCheckout(): void {
    if (!this.payloadConfig()?.feeId) return;

    this.checkoutService
      .prepareZimswitchCheckout(this.payloadConfig())
      .pipe(
        catchError((error) => {
          console.error('Checkout preparation failed', error);
          this.patchWidgetState({ error: 'Failed to prepare checkout. Please try again.', isLoading: false });
          throw error;
        })
      )
      .subscribe((res: CheckoutResponse) => {
        const { id, integrity } = res;
        const shopperUrl =
          this.paymentRedirectUrl +
          this.referenceNumber +
          '?serverUrl=' +
          this.checkoutService.baseUrl +
          '&frontendUrl=' +
          this.checkoutService.frontendUrl;

        this.clearPaymentForm();
        this.createPaymentForm(shopperUrl);
        this.loadPaymentWidget(id, integrity);
      });
  }

  private clearPaymentForm(): void {
    const container = this.paymentContainer().nativeElement;
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
  }

  private createPaymentForm(shopperUrl: string): void {
    const form = this.renderer.createElement('form');
    this.renderer.setAttribute(form, 'action', shopperUrl);
    this.renderer.setAttribute(form, 'class', 'paymentWidgets');
    this.renderer.setAttribute(form, 'data-brands', 'PRIVATE_LABEL');
    this.renderer.appendChild(this.paymentContainer().nativeElement, form);
  }

  private async loadPaymentWidget(checkoutId: string, integrity: string, retries = 3): Promise<void> {
    const scriptUrl = `${this.config.zimswitchGateway}paymentWidgets.js?checkoutId=${checkoutId}`;

    for (let i = 0; i < retries; i++) {
      try {
        await this.loadScript(scriptUrl, integrity, 'anonymous');
        return;
      } catch (error) {
        if (i === retries - 1) {
          this.patchWidgetState({ error: 'Failed to load payment widget after multiple attempts', isLoading: false });
          throw error;
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }
  }

  private loadScript(src: string, integrity: string, crossOrigin: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => reject(new Error('Payment widget load timeout')), this.WIDGET_LOAD_TIMEOUT);

      document.querySelector('script[src*="paymentWidgets.js"]')?.remove();

      const script = this.renderer.createElement('script');
      script.src = src;
      script.integrity = integrity;
      script.crossOrigin = crossOrigin;
      script.async = true;

      script.onload = () => {
        clearTimeout(timeoutId);
        this.patchWidgetState({ isScriptLoaded: true });
        resolve();
      };
      script.onerror = (error: any) => {
        clearTimeout(timeoutId);
        this.patchWidgetState({ error: 'Failed to load payment widget' });
        reject(error);
      };

      document.body.appendChild(script);
    });
  }
}

